import { useCallback, useRef } from "react";

import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";

import { PaymentIncidentsService } from "../services/paymentIncidents.service";
import type { PaymentIncidentRefundCreated } from "../types/paymentIncidents.types";

/**
 * Refunding the client, behind the TOTP. One idempotency key per dialog: a retry after a lost answer reuses
 * it and the backend replays the first refund. A second refund under another key cannot credit either, since
 * the backend keeps one refund per incident (409 `incident_already_refunded`), so no attempt needs to outlive
 * the dialog: after any failure the caller reads the incident again, and its `refund.top_up` says what
 * happened.
 */
export function useIncidentRefund(incidentId: string) {
  // The answer waits for the credit; the dialog shows the money travelling instead of the challenge.
  const twoFactor = useTwoFactorAction<PaymentIncidentRefundCreated>({ closeOnSend: true });
  const keyRef = useRef<string>(crypto.randomUUID());
  const { start } = twoFactor;

  const submit = useCallback(
    (amount: string, note: string | undefined) =>
      start((code) =>
        PaymentIncidentsService.refund(
          incidentId,
          { amount: amount.trim(), note: note?.trim() || undefined, idempotency_key: keyRef.current },
          code,
        ),
      ),
    [incidentId, start],
  );

  /** A different refund (another amount or note) is a new operation. */
  const renewKey = useCallback(() => {
    keyRef.current = crypto.randomUUID();
  }, []);

  return { submit, renewKey, twoFactor };
}
